<template>
    <b-modal
      id="mainLogIn"
      modal-class="modal-cover"
      v-model="isVisible"
      hide-footer
      content-class="shadow-lg"
      centered
    >

          <!-- Modal -->
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-body">
          <div class="trip-login-wrap" :dir="isHebrew ? 'rtl' : 'ltr'">
            <h2 class="access-with-title">{{ t('accessWith') }}</h2>

            <!-- Social login buttons - FIRST to encourage OAuth2 -->
            <div class="social-login-buttons" v-if="!showPasswordField">
              <button 
                v-if="!isFacebookBrowser"
                class="social-login-btn google-btn"
                @click="loginToAppWithGoogle">
                <img 
                  src="https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/icons%2Flog_in%2Fgoogle.png?alt=media&token=60857ff1-650c-464f-861b-2aabdcb78df7" 
                  alt="Google"
                  class="social-icon"
                >
                <span>{{ t('continueWithGoogle') }}</span>
              </button>
              <button
                v-else
                class="social-login-btn google-btn"
                @click="we_are_not_suporint_it_from_facebook_browser">
                <img 
                  src="https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/icons%2Flog_in%2Fgoogle.png?alt=media&token=60857ff1-650c-464f-861b-2aabdcb78df7" 
                  alt="Google"
                  class="social-icon"
                >
                <span>{{ t('continueWithGoogle') }}</span>
              </button>
              
            </div>

            <p
              v-if="oauthHintKey && !showPasswordField"
              :class="[
                'oauth-hint',
                suggestedOAuthMethod ? `hint-${suggestedOAuthMethod}` : 'hint-generic',
                { 'oauth-hint-clickable': suggestedOAuthMethod === 'google' }
              ]"
              @click="handleOauthHintClick"
            >
              <span class="oauth-hint-icon">⚡</span>
              {{ t(oauthHintKey) }}
            </p>

            <div class="trip-border-content" v-if="!showPasswordField">
              <p>{{ t('or') }}</p>
            </div>

            <!-- Email input section - SECOND -->
            <div class="email-login-section" v-if="!showPasswordField">
              <div class="trip-login-field">
                <b-form-input
                  dir="ltr"
                  :value="email"
                  @input="updateEmail"
                  @keyup.enter="checkEmailAndContinue"
                  autocapitalize="none"
                  id="email"
                  :placeholder="t('emailPlaceholder')"
                  :class="['log_in_medium', isHebrew ? 'text-right' : 'text-left']"
                  type="email"
                  required>
                </b-form-input>
              </div>
              <button 
                class="continue-btn"
                type="button"
                @click="checkEmailAndContinue"
                :disabled="!email || isCheckingEmail">
                {{ isCheckingEmail ? t('loading') : t('continueButton') }}
              </button>
              <div class="checking-indicator" v-if="isCheckingEmail">
                <span class="checking-spinner"></span>
                {{ t('checkingAccount') }}
              </div>
            </div>

            <!-- Password input section (shown when email exists) -->
            <div class="password-login-section" v-if="showPasswordField">
              <button 
                class="back-to-email-btn"
                type="button"
                @click="returnToEmailInput">
                <span class="back-arrow">←</span>
                <span class="back-label">{{ t('back') || 'Back' }}</span>
              </button>
              <div class="trip-login-field">
                <b-form-input
                  dir="ltr"
                  :value="email"
                  disabled
                  autocapitalize="none"
                  id="email-display"
                  :class="['log_in_medium', isHebrew ? 'text-right' : 'text-left']"
                  type="email">
                </b-form-input>
              </div>
              <div class="trip-login-field">
                <b-form-input
                  dir="ltr"
                  @input="updatePassword"
                  @keyup.enter="loginToApp"
                  autocapitalize="none"
                  :placeholder="t('passwordPlaceholder')"
                  :class="['log_in_medium', isHebrew ? 'text-right' : 'text-left']"
                  type="password"
                  ref="passwordInput">
                </b-form-input>
              </div>
              <button 
                id="trip-login-btn"
                type="button"
                @click="loginToApp">
                {{ t('loginButton') }}
              </button>
            </div>

            <p class="privacy-agreement">
              {{ t('socialLoginAgreement') }} 
              <router-link to="/privacy_policy" class="privacy-link" target="_blank">{{ t('privacyPolicy') }}</router-link>
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- End modal -->
  </b-modal>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import { translationMixin } from '@/mixins/translationMixin';
import api from '@/api/appauth';

export default {
  mixins: [translationMixin],
  props: ["value"],
  data() {
    return {
      isFacebookBrowser: false,
      showPasswordField: false,
      isCheckingEmail: false,
      authMethods: [],
      showSocialHint: false,
      suggestedOAuthMethod: null,
    };
  },
  computed: {
    ...mapGetters(['email', 'password']),
    isVisible: {
      set(val) {
        this.$emit("input", val);
      },
      get() {
        return this.value;
      },
    },
    oauthHintKey() {
      if (!this.showSocialHint) {
        return null;
      }
      if (this.suggestedOAuthMethod === 'google') {
        return 'loginOAuthHintGoogle';
      }
      return 'loginOAuthHintGeneric';
    }
  },
  watch: {
    isVisible(newVal) {
      if (!newVal) {
        this.resetLoginFlow();
      }
    }
  },
  methods: 
  {
    ...mapActions([
      'loginToAppWithGoogle',
      'showModalWithParameters',
      'authenticate',
    ]),
    resetLoginFlow() {
      this.showPasswordField = false;
      this.showSocialHint = false;
      this.suggestedOAuthMethod = null;
      this.authMethods = [];
      this.isCheckingEmail = false;
      this.$store.commit('setPassword', '');
    },
    returnToEmailInput() {
      this.resetLoginFlow();
    },
    we_are_not_suporint_it_from_facebook_browser() {
      this.showModalWithParameters({
        errorNumber: 'not_supporting_FB_or_instagram_browsers',
        type: 'textConverter',
      });
    },
    detectFacebookBrowser() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      this.isFacebookBrowser = /facebook|Facebook|FB|Instagram|FB_IAB|FBAN|FBAV|FBIOS/.test(userAgent);
    },
    validateEmail(email) {
      // Remove any whitespace from the email
      const trimmedEmail = email.trim();
      
      // Check if email contains spaces after trimming
      if (trimmedEmail.includes(' ')) {
        this.showModalWithParameters({
          errorNumber: 'invalid_email_spaces',
          type: 'textConverter'
        });
        return false;
      }
      
      // Basic email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        this.showModalWithParameters({
          errorNumber: 'invalid_email_format',
          type: 'textConverter'
        });
        return false;
      }
      
      return true;
    },
    updateEmail(value) {
      this.$store.commit('setEmail', value);
    },
    updatePassword(value) {
      this.$store.commit('setPassword', value);
    },
    async checkEmailAndContinue() {
      if (!this.validateEmail(this.email)) {
        return;
      }
      
      const trimmedEmail = this.email.trim();
      this.$store.commit('setEmail', trimmedEmail);
      
      this.showPasswordField = false;
      this.showSocialHint = false;
      this.suggestedOAuthMethod = null;
      this.authMethods = [];
      this.isCheckingEmail = true;
      
      try {
        const { data } = await api.checkEmailExists(trimmedEmail);
        if (data.status !== 'success') {
          throw new Error('check_user_auth_methods_failed');
        }

        if (!data.registered) {
          this.showSocialHint = false;
          this.showPasswordField = false;
          this.goToNewUserForm();
          return;
        }

        this.authMethods = Array.isArray(data.available_methods)
          ? data.available_methods
              .map(method => (typeof method === 'string' ? method.toLowerCase() : null))
              .filter(Boolean)
          : [];

        const providerRaw = (data.oauth_provider || '').toLowerCase();
        this.suggestedOAuthMethod = providerRaw && providerRaw !== 'none' ? providerRaw : null;
        this.notifySocialLogin(this.suggestedOAuthMethod);

        if (data.has_password) {
          this.showPasswordField = true;
          this.showSocialHint = false;
          this.focusPasswordField();
          return;
        }

        this.showPasswordField = false;
        this.showSocialHint = true;

        if (!this.suggestedOAuthMethod) {
          if (this.authMethods.includes('google')) {
            this.suggestedOAuthMethod = 'google';
          } else if (this.authMethods.length) {
            this.suggestedOAuthMethod = 'generic';
          }
        }

        if (!this.suggestedOAuthMethod) {
          this.suggestedOAuthMethod = 'generic';
        }
        this.notifySocialLogin(this.suggestedOAuthMethod);
      } catch (error) {
        console.error('Failed to check email auth methods', error);
        this.showPasswordField = true;
        this.showSocialHint = false;
        this.focusPasswordField();
      } finally {
        this.isCheckingEmail = false;
      }
    },
    focusPasswordField() {
      if (!this.showPasswordField) {
        return;
      }
      this.$nextTick(() => {
        if (!this.$refs || !this.$refs.passwordInput) {
          return;
        }
        const inputComponent = this.$refs.passwordInput;
        const inputElement = inputComponent.$el
          ? inputComponent.$el.querySelector('input')
          : inputComponent.$refs && inputComponent.$refs.input;
        if (inputElement && typeof inputElement.focus === 'function') {
          inputElement.focus();
        }
      });
    },
    notifySocialLogin(provider) {
      if (!provider || provider !== 'google') {
        return;
      }

      const messages = {
        google: this.t('loginOAuthHintGoogle'),
      };

      const message = messages[provider];
      if (!message) {
        return;
      }
      if (this.$bvToast && typeof this.$bvToast.toast === 'function') {
        this.$bvToast.toast(message, {
          title: 'Google',
          variant: 'info',
          solid: true,
          autoHideDelay: 4000,
        });
      } else {
        this.showModalWithParameters({
          errorNumber: 'oauth_login_hint',
          textToAdd: message,
          type: 'textConverter',
        });
      }
    },
    handleOauthHintClick() {
      if (this.suggestedOAuthMethod !== 'google') {
        return;
      }
      if (this.isFacebookBrowser) {
        this.we_are_not_suporint_it_from_facebook_browser();
        return;
      }
      this.loginToAppWithGoogle();
    },
    loginToApp() {
      if (!this.password || this.password.trim() === '') {
        this.showModalWithParameters({
          errorNumber: 'password_required',
          type: 'textConverter'
        });
        return;
      }
      
      // Proceed with login
      this.$store.dispatch('showModal', "");
      this.$store.dispatch('authenticate');
    },
    goToNewUserForm() {
      this.$store.dispatch('showModal', "mainRegistration");
    },
  },
  mounted() {
    this.detectFacebookBrowser();
  },

};
</script>
<style scoped>

.margin-top{
  margin-top: 1rem;
}

/* modal */
.modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: 40px 30px;
}
.modal-dialog {
  max-width: 400px;
  
  margin: 1.75rem auto!important;
  margin-top: 10%!important;
  
}
.modal-content {
  background: #FFFFFF;
  box-shadow: 4px 4px 4px rgb(0 0 0 / 10%);
  border-radius: 25px;
}
.trip-login-wrap{
  text-align: center;
}

.access-with-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #2E2E3A;
  margin-bottom: 30px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.social-login-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.oauth-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: #5f3b00;
  margin: -8px 0 18px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #f5c16c;
  background: #fff9ee;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.oauth-hint-clickable {
  cursor: pointer;
}

.oauth-hint-clickable:hover {
  filter: brightness(0.98);
}

.oauth-hint-icon {
  font-size: 16px;
}

.oauth-hint.hint-google {
  border-color: #8ab4ff;
  background: rgba(66, 133, 244, 0.08);
  color: #1f3d7a;
}

.oauth-hint.hint-facebook {
  border-color: #b5c9ff;
  background: rgba(24, 119, 242, 0.08);
  color: #0f3175;
}

.oauth-hint.hint-generic {
  border-color: #34d0b6;
  background: rgba(52, 208, 182, 0.12);
  color: #0f4c3a;
}

.social-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  height: 48px;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  background: #FFFFFF;
  font-weight: 500;
  font-size: 14px;
  color: #2E2E3A;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0 20px;
}

.social-login-btn:hover {
  background: #F5F5F5;
  border-color: #C0C0C0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.social-login-btn:active {
  transform: scale(0.98);
}

.social-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.google-btn {
  border-color: #4285F4;
  color: #1f3d7a;
}

.google-btn:hover {
  background: #E8F0FD;
  border-color: #4285F4;
}

.trip-login-wrap[dir="rtl"] .social-login-buttons {
  direction: rtl;
}

.trip-login-wrap[dir="rtl"] .social-login-btn {
  flex-direction: row-reverse;
  text-align: right;
}

.trip-login-wrap[dir="rtl"] .social-login-btn span {
  text-align: right;
}

.trip-login-wrap[dir="rtl"] .social-icon {
  margin-left: 0;
  margin-right: 0;
}

.privacy-agreement {
  font-size: 11px;
  line-height: 16px;
  color: #666666;
  text-align: center;
  margin: 0;
  padding: 0 10px;
}

.privacy-link {
  color: #2E2E3A;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;
}

.privacy-link:hover {
  color: #07A0C3;
  text-decoration: underline;
}

.email-login-section,
.password-login-section {
  margin-bottom: 20px;
}

.continue-btn {
  width: 100%;
  height: 48px;
  background: #2E2E3A;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  font-size: 14px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;
}

.continue-btn:hover:not(:disabled) {
  background: #1a1a24;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.continue-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.continue-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.checking-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #2E2E3A;
  margin-top: 8px;
}

.checking-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(47, 47, 58, 0.2);
  border-top-color: #2E2E3A;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.back-to-email-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #2E2E3A;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 0 8px 26px;
  margin-bottom: 10px;
  text-align: left;
  transition: color 0.2s ease;
}

.back-to-email-btn:hover {
  color: #07A0C3;
}

.back-arrow {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
}

.trip-login-wrap[dir="rtl"] .back-to-email-btn {
  text-align: right;
  padding-left: 26px;
  padding-right: 0;
}

.trip-login-field input:disabled {
  background-color: #F5F5F5;
  opacity: 0.7;
  cursor: not-allowed;
}

.trip-login-field{
  margin-bottom: 10px;
}
.trip-login-field input{
  height: 48px;
  width: 100%;
  border: 1px solid #C1C0C0;
  border-radius: 4px;
  font-size: 16px;
  line-height: 22px;
  padding: 12px 15px!important;
  color: black;
}

.trip-login-field input.text-right {
  text-align: right;
}

.trip-login-field input.text-left {
  text-align: left;
}
button#trip-login-btn, 
button#trip-login-btn2 {
  width: 100%;
  height: 48px;
  background: #2E2E3A;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  font-size: 14px;
  color: white;
  margin-top: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

button#trip-login-btn:hover {
  background: #1a1a24;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
button#trip-login-btn2{
  background-color:  #07A0C3;
}

.trip-login-wrap h3{
  font-weight: 800;
  font-size: 18px;
  line-height: 21px;
  color: #2E2E3A;
  margin-bottom: 15px;
  text-align: center
}
.trip-border-content{
  text-align: center;
  margin-top: 20px;
  margin-bottom: 12px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.trip-border-content p{
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  color: #2E2E3A;
  text-align: center;
  position: relative;
  z-index: 2;
  background-color: #fff;
  padding: 0 20px;
  margin: 0;
  white-space: nowrap;
  display: inline-block;
}
.trip-border-content p:before, 
.trip-border-content p:after {
  content: "";
  position: absolute;
  top: 50%;
  width: 35%;
  height: 1px;
  background-color: #2E2E3A;
  z-index: 0;
}
.trip-border-content p:before {
  left: 0;
}
.trip-border-content p:after {
  right: 0;
}
.trip-login-bottom-btn{
  margin-top: 25px;
}
.trip-login-wrap > * {
  margin-bottom: 10px;
}
.trip-login-wrap > *:last-child {
  margin-bottom: 0;
}

.trip-login-bottom-btn h5{
  font-size: 14px;
  line-height: 12px;
  color: #2E2E3A;
  margin-bottom: 5px;
}

button.trip-login-btn {
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: #FFFFFF;
  width: 129px;
  height: 30px;
  background: #07A0C3;
  border-radius: 22px;
  border: none;
}
.trip-login-check {
  text-align: right;
}
.trip-login-check h5{
  font-size: 9px;
  line-height: 11px;
  text-align: right;
  color: #2E2E3A;
  margin-top: 7px;
}
.trip-login-check label{
  font-size: 10px;
  line-height: 12px;
  color: #2E2E3A;
}
.trip-login-check input {
  padding: 0;
  height: initial;
  width: initial;
  margin-bottom: 0;
  display: none;
  cursor: pointer;
}

.trip-login-check label {
  position: relative;
  cursor: pointer;
}

.trip-login-check label:before {
  content:'';
  -webkit-appearance: none;
  background-color: transparent;
  border: 1px solid #2E2E3A;
  padding: 0 6.5px;
  position: relative;
  vertical-align: middle;
  cursor: pointer;
  margin-left: 5px;
  border-radius: 3px;
}

.trip-login-check input:checked + label:after {
  content: '';
  display: block;
  position: absolute;
  top: 3px;
  right: 6px;
  width: 5px;
  height: 8px;
  border: solid #2E2E3A;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* contact page */
.trip-card-top.trip-card-top2{
  text-align: left;
  position: relative;
}
.trip-card-top h4 {
  font-weight: 600;
  font-size: 30px;
  line-height: 41px;
  color: #0D2F29;
  display: inline-block;
  position: absolute;
  top: 0;
  margin: 0;
  left: 50%;
  transform: translateX(-50%);
}
.trip-contact-wrap h4{
  display: none;
}
.trip-contact-field {
  margin-bottom: 15px;
}
.trip-contact-field label{
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: right;
  color: #110703;
  display: block;
  margin-bottom: 5px;
  margin-right: 40px;
  }
  .trip-contact-field label span{
    color: #E42424;
  }
  
  .trip-contact-input{
    position: relative;
  }
  .trip-contact-field img{
    width: 100%;
  }
  .trip-contact-input input, 
  .trip-contact-input textarea{
    height: 56px;
    width: 100%;
    background: #FFFFFF;
    border: 2px solid #07A0C3!important;
    border-radius: 45px;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
    text-align: right;
    padding: 10px 50px 10px 160px!important;
    color: rgba(13, 47, 41, 0.5)!important;

  }
  .trip-contact-input textarea{
    height: 300px;
  }
  .trip-input-icon {
    position: absolute;
    top: 13px;
    right: 25px;
  }
  .trip-contact-input p{
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    margin:15px 0 0;
    color: rgba(228, 36, 36, 0.6)
  }
.trip-contact-input input::-webkit-input-placeholder, 
.trip-contact-input textarea::-webkit-input-placeholder{ /* WebKit, Blink, Edge */
    color: rgba(13, 47, 41, 0.5);
    opacity: 1;
}
.trip-contact-input input:-moz-placeholder, 
.trip-contact-input textarea:-moz-placeholder{ /* Mozilla Firefox 4 to 18 */
  color: rgba(13, 47, 41, 0.5);
   opacity:  1;
}
.trip-contact-input input::-moz-placeholder, 
.trip-contact-input textarea::-moz-placeholder{ /* Mozilla Firefox 19+ */
  color: rgba(13, 47, 41, 0.5);
   opacity:  1;
}
.trip-contact-input input:-ms-input-placeholder, 
.trip-contact-input textarea:-ms-input-placeholder{ /* Internet Explorer 10-11 */
  color: rgba(13, 47, 41, 0.5);
   opacity: 1;
}
.trip-contact-input input::-ms-input-placeholder, 
.trip-contact-input textarea::-ms-input-placeholder{ /* Microsoft Edge */
  color: rgba(13, 47, 41, 0.5);
   opacity: 1;
}

.trip-contact-input input::placeholder, 
.trip-contact-input textarea::placeholder{ /* Most modern browsers support this now. */
  color: rgba(13, 47, 41, 0.5);
   opacity: 1;
}

button#submit-btn1 {
  height: 56px;
  background: #0D2F29;
  border-radius: 12px;
  width: 100%;
  font-weight: 700;
  font-size: 20px;
  line-height: 27px;
  text-align: center;
  color: #FFFFFF;
}
button#submit-btn2 {
    width: 188px;
    height: 42px;
    background: #DD6E42;
    border-radius: 45px;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    color: #FFFFFF;
    border: none;
    margin: 0 auto;
}

.trip-submit-button{
  margin-top: 50px;
  text-align: center;
}

.trip-contact-form-section {
  padding-bottom: 150px;
}
.trip-contact-siderbar{
  background: rgba(225, 246, 251, 0.5);
  border-radius: 18px;
  padding: 25px;
  margin-left: 50px;
}
.trip-contact-siderbar h5{
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  color: #E42424;
}

.trip-card2-thumb-resposive {
  display: none;
}
.trip-card2-thumb-resposive img{
  width: 100%;
  height: 100%;
}

.close-icon {
  border: 1px solid transparent;
  background-color: transparent;
  display: inline-block;
  vertical-align: middle;
  outline: 0;
  cursor: pointer;
  position: absolute;
  left: 13px;
  top: 13px;
  font-weight: 800;
  font-size: 18px;
  line-height: 24px;
  text-align: right;
  color: #FF0000;
}
.close-icon img{
  width: auto;
}
.input-remove:not(:valid) ~ .close-icon {
	display: none;
}

.forgot-password-wrap {
  text-align: left;
  margin: -10px 0 15px;
}

.forgot-password-link {
  color: #007bff;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
}

.forgot-password-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* Language-specific styles */
.trip-login-wrap[dir="rtl"] {
  text-align: center;
}

.trip-login-wrap[dir="ltr"] {
  text-align: center;
}

.trip-login-wrap[dir="ltr"] .access-with-title,
.trip-login-wrap[dir="ltr"] .privacy-agreement {
  text-align: center;
}

.trip-login-wrap[dir="rtl"] .access-with-title,
.trip-login-wrap[dir="rtl"] .privacy-agreement {
  text-align: center;
}

/* Social login buttons - always centered */
.social-login-buttons {
  direction: ltr;
}

/* Privacy agreement text direction */
.trip-login-wrap[dir="rtl"] .privacy-agreement {
  direction: rtl;
  text-align: center;
}

.trip-login-wrap[dir="ltr"] .privacy-agreement {
  direction: ltr;
  text-align: center;
}

/* Language toggle positioning */
.language-toggle-container {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
}

/* Customize language toggle for modal */
.language-toggle-container .language-toggle .language-btn {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 4px 8px;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.language-toggle-container .language-toggle .language-btn:hover {
  background: rgba(255, 255, 255, 1);
  border-color: #34D0B6;
}











</style>